import { Checkbox, Group, Text, Tooltip } from "@mantine/core"
import { IconInfoCircle } from "@tabler/icons-react"
import { createColumnHelper, DisplayColumnDef } from "@tanstack/react-table"
import { Dispatch, SetStateAction, useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  BatchUpdateOperationItem,
  ComponentOperationItem,
  MaterialOperationItem,
  ProductOperationItem,
} from "@tfg/types/schemas/modules/traceability/queries/find-batch-update-operations.response.dto"

import { CopyToClipboard } from "~/components/CopyToClipboard.tsx"
import { ProductionSiteDisplay } from "~/pages/SupplierPortal/TraceabilityRequests/components/ProductionSiteDisplay.tsx"
import { useAuth } from "~/store/auth-context.tsx"

type Props = {
  batchOperations: BatchUpdateOperationItem[]
  key: string
  checkList: number[]
  setCheckList: Dispatch<SetStateAction<number[]>>
}

export function useBatchColumns({
  batchOperations,
  key,
  checkList,
  setCheckList,
}: Props) {
  const columnHelper = createColumnHelper<BatchUpdateOperationItem>()
  const { isAdmin } = useAuth()
  const { t } = useTranslation(["traceability"])

  return useMemo(() => {
    const customFieldColumns: DisplayColumnDef<
      BatchUpdateOperationItem,
      unknown
    >[] = []
    if (batchOperations[0]?.customFieldsValues) {
      Object.keys(batchOperations[0]?.customFieldsValues ?? {}).map((key) => {
        customFieldColumns.push(
          columnHelper.display({
            id: key,
            header: key,
            cell: (info) => {
              if (!info.row.original.customFieldsValues) {
                return null
              }
              return (
                <Text>{info.row.original.customFieldsValues[key]?.value}</Text>
              )
            },
          }),
          columnHelper.display({
            id: `${key} document name`,
            header: t("batchModal.labels.documentProof"),
            cell: (info) => {
              const customField = info.row.original.customFieldsValues?.[key]
              if (!customField) {
                return null
              }
              return (
                <Text>
                  {customField.document?.originalName ??
                    t("batchModal.noDocument")}
                </Text>
              )
            },
          })
        )
      })
    }

    const columns = [
      columnHelper.display({
        header: undefined,
        id: "checkbox",
        cell: (info) => (
          <Checkbox
            onChange={() =>
              setCheckList((prev) =>
                prev.includes(info.row.original.id)
                  ? prev.filter((id) => id !== info.row.original.id)
                  : [...prev, info.row.original.id]
              )
            }
            checked={checkList.includes(info.row.original.id)}
          />
        ),
      }),
      //GENERAL COLUMNS
      batchOperations.some((row) => row.informationRequestItemReference)
        ? columnHelper.display({
            id: "informationRequestItemReference",
            header: () => (
              <Group wrap="nowrap" gap={0}>
                {t("batchModal.labels.informationRequestItemReference")}
                <Tooltip
                  label={t(
                    "batchModal.labels.informationRequestItemReferenceHint"
                  )}
                  multiline={true}
                >
                  <IconInfoCircle />
                </Tooltip>
              </Group>
            ),
            cell: (info) => (
              <Text>{info.row.original.informationRequestItemReference}</Text>
            ),
          })
        : null,
      batchOperations.some((row) => row.productName)
        ? columnHelper.display({
            header: t("batchModal.labels.productName"),
            cell: (info) => <Text>{info.row.original.productName}</Text>,
          })
        : null,
      batchOperations.some((row) => row.productReference)
        ? columnHelper.display({
            header: t("batchModal.labels.productReference"),
            cell: (info) => <Text>{info.row.original.productReference}</Text>,
          })
        : null,
      batchOperations.some((row) => row.contact?.email)
        ? columnHelper.display({
            header: t("batchModal.labels.contact"),
            cell: (info) => <Text>{info.row.original.contact?.email}</Text>,
          })
        : null,
      columnHelper.display({
        header: t("batchModal.labels.productionSite"),
        cell: (info) => (
          <ProductionSiteDisplay
            productionSiteId={info.row.original.productionSiteId}
          />
        ),
      }),
      columnHelper.display({
        header: t("batchModal.labels.clientName"),
        cell: (info) => <Text>{info.row.original.clientName}</Text>,
      }),
      ...(key === "reference"
        ? [
            columnHelper.display({
              header: t("batchModal.labels.reference"),
              cell: (info) => <Text>{info.row.original.reference}</Text>,
            }),
          ]
        : []),
      ...(isAdmin
        ? [
            columnHelper.display({
              header: t("batchModal.labels.productId"),
              cell: (info) => (
                <CopyToClipboard
                  text={info.row.original.productId?.toString() ?? ""}
                  color="violet"
                />
              ),
            }),
          ]
        : []),
      //PRODUCT SPECIFIC COLUMNS
      batchOperations.some(
        (row) => isProductOperationItem(row) && row.productCollection
      )
        ? columnHelper.display({
            header: t("batchModal.labels.productCollection"),
            cell: (info) =>
              isProductOperationItem(info.row.original) && (
                <Text>{info.row.original.productCollection}</Text>
              ),
          })
        : null,
      batchOperations.some(
        (row) => isProductOperationItem(row) && row.productCollection
      )
        ? columnHelper.display({
            header: t("batchModal.labels.productColor"),
            cell: (info) =>
              isProductOperationItem(info.row.original) && (
                <Text>{info.row.original.productColor}</Text>
              ),
          })
        : null,
      batchOperations.some(
        (row) => isProductOperationItem(row) && row.productCollection
      )
        ? columnHelper.display({
            header: t("batchModal.labels.purchaseOrder"),
            cell: (info) =>
              isProductOperationItem(info.row.original) && (
                <Text>{info.row.original.purchaseOrder}</Text>
              ),
          })
        : null,
      batchOperations.some(
        (row) => isComponentOperationItem(row) && row.componentReference
      )
        ? columnHelper.display({
            header: t("batchModal.labels.componentReference"),
            cell: (info) =>
              isComponentOperationItem(info.row.original) && (
                <Text>{info.row.original.componentReference}</Text>
              ),
          })
        : null,
      batchOperations.some(
        (row) => isComponentOperationItem(row) && row.componentName
      )
        ? columnHelper.display({
            header: t("batchModal.labels.componentName"),
            cell: (info) =>
              isComponentOperationItem(info.row.original) && (
                <Text>{info.row.original.componentName}</Text>
              ),
          })
        : null,
      batchOperations.some(
        (row) => isMaterialOperationItem(row) && row.materialReference
      )
        ? columnHelper.display({
            id: "materialReference",
            header: t("batchModal.labels.materialReference"),
            cell: (info) =>
              isMaterialOperationItem(info.row.original) && (
                <Text>{info.row.original.materialReference}</Text>
              ),
          })
        : null,
      batchOperations.some(
        (row) => isMaterialOperationItem(row) && row.materialName
      )
        ? columnHelper.display({
            id: "materialReference",
            header: t("batchModal.labels.materialName"),
            cell: (info) =>
              isMaterialOperationItem(info.row.original) && (
                <Text>{info.row.original.materialName}</Text>
              ),
          })
        : null,
      batchOperations.some(
        (row) => isMaterialOperationItem(row) && row.materialReference
      )
        ? columnHelper.display({
            id: "materialProportion",
            header: t("batchModal.labels.materialProportion"),
            cell: (info) =>
              isMaterialOperationItem(info.row.original) && (
                <Text>{info.row.original.materialProportion}</Text>
              ),
          })
        : null,
      batchOperations.some((row) => row.purchaseOrderRef)
        ? columnHelper.display({
            header: t("batchModal.labels.purchaseOrderRef"),
            cell: (info) => <Text>{info.row.original.purchaseOrderRef}</Text>,
          })
        : null,
    ].filter(Boolean) // this filters any null values for conditional rendering,
    return [...columns, ...customFieldColumns]
  }, [batchOperations, columnHelper, t, key, isAdmin, checkList, setCheckList])
}

function isComponentOperationItem(
  item: BatchUpdateOperationItem
): item is ComponentOperationItem {
  return "componentReference" in item
}

function isProductOperationItem(
  item: BatchUpdateOperationItem
): item is ProductOperationItem {
  return "productCollection" in item
}

function isMaterialOperationItem(
  item: BatchUpdateOperationItem
): item is MaterialOperationItem {
  return "materialReference" in item
}
